/**
 * Bulk Onboarding — one CSV, many campuses.
 *
 * Super-admin-only page. User pastes/uploads a CSV where each row is one
 * campus. The importer figures out which tenants/institutions are new vs
 * existing and creates only what's missing.
 */
import { useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, Download, Loader2, CheckCircle2, XCircle, FileText, Rows3, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const REQUIRED_COLS = ["tenant_name", "institution_name", "campus_name"];
const ALL_COLS = ["tenant_name", "institution_name", "campus_name", "city", "state", "country"];

const TEMPLATE_CSV = `tenant_name,institution_name,campus_name,city,state,country
Bright Future Academy,Bright Future Kids,Delhi Main,Delhi,DL,IN
Bright Future Academy,Bright Future Kids,Noida,Noida,UP,IN
Bright Future Academy,Bright Future Junior,Gurgaon,Gurgaon,HR,IN
Zenith Public,Zenith Public School,Bengaluru,Bengaluru,KA,IN
`;

/** Minimal CSV parser that respects quoted values (handles commas inside "..."). */
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };

  const splitLine = (line) => {
    const out = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else cur += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") { out.push(cur.trim()); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur.trim());
    return out;
  };

  const headers = splitLine(lines[0]).map((h) => h.toLowerCase().trim());
  const rows = lines.slice(1).map((l) => {
    const cells = splitLine(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
    return obj;
  });
  return { headers, rows };
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "edukampus-bulk-onboard-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function BulkOnboard() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const [csvText, setCsvText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => (csvText.trim() ? parseCSV(csvText) : { headers: [], rows: [] }), [csvText]);
  const missing = REQUIRED_COLS.filter((c) => !parsed.headers.includes(c));
  const validRows = parsed.rows.filter(
    (r) => r.tenant_name && r.institution_name && r.campus_name,
  );
  const invalidCount = parsed.rows.length - validRows.length;

  const distinctTenants = useMemo(
    () => new Set(validRows.map((r) => r.tenant_name.trim().toLowerCase())).size,
    [validRows],
  );
  const distinctInsts = useMemo(
    () => new Set(validRows.map((r) => `${r.tenant_name.trim().toLowerCase()}::${r.institution_name.trim().toLowerCase()}`)).size,
    [validRows],
  );

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
    setResult(null);
  };

  const doImport = async () => {
    if (!validRows.length) return;
    setLoading(true); setResult(null);
    try {
      const rows = validRows.map((r) => ({
        tenant_name: r.tenant_name.trim(),
        institution_name: r.institution_name.trim(),
        campus_name: r.campus_name.trim(),
        city: r.city?.trim() || null,
        state: r.state?.trim() || null,
        country: (r.country?.trim() || "IN"),
      }));
      const { data } = await api.post("/admin/bulk-onboard", { rows });
      setResult(data);
      toast.success(
        `Import complete — ${data.campuses_created} created, ${data.campuses_reused} already existed`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  if (!isSuper) {
    return (
      <AppShell>
        <div className="p-8 max-w-2xl mx-auto text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-display font-bold text-xl mb-2">Super admin only</h1>
          <p className="text-sm text-muted-foreground">This page is available to platform admins only.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="bulk-onboard-page">
        {/* Hero */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground mb-2">
            <Rows3 className="h-3 w-3 inline -mt-0.5 mr-1.5" /> Bulk Onboarding
          </div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight leading-none">Onboard many tenants at once</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl leading-relaxed">
            Drop a CSV where each row is one campus. The importer figures out which tenants and
            institutions are new vs existing — it&apos;s <b>idempotent by name</b>, so re-running the
            same file twice never creates duplicates.
          </p>
        </div>

        {/* Step 1 — get the template */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">1. Download the template</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-button">
              <Download className="h-4 w-4 mr-2" /> Download CSV template
            </Button>
            <div className="text-xs text-muted-foreground font-mono">
              Columns: <b>tenant_name, institution_name, campus_name</b>, city, state, country
            </div>
          </CardContent>
        </Card>

        {/* Step 2 — upload or paste */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">2. Upload a CSV or paste it below</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium cursor-pointer transition-colors">
                <Upload className="h-4 w-4" />
                Upload file
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
              </label>
              <span className="text-xs text-muted-foreground">or paste below</span>
            </div>
            <Textarea
              placeholder={"tenant_name,institution_name,campus_name,city,state,country\n..."}
              value={csvText}
              onChange={(e) => { setCsvText(e.target.value); setResult(null); }}
              rows={8}
              className="font-mono text-xs"
              data-testid="csv-textarea"
            />
          </CardContent>
        </Card>

        {/* Step 3 — preview */}
        {parsed.rows.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                3. Preview
                <Badge variant="secondary" className="font-mono">{parsed.rows.length} rows</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {missing.length > 0 && (
                <div className="text-sm border border-destructive/40 bg-destructive/5 text-destructive p-3 rounded-md" data-testid="missing-cols-error">
                  Missing required column(s): <b>{missing.join(", ")}</b>
                </div>
              )}
              {invalidCount > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-500 bg-amber-100/40 dark:bg-amber-900/20 border border-amber-500/30 p-2.5 rounded-md">
                  ⚠ {invalidCount} row{invalidCount === 1 ? "" : "s"} skipped — missing tenant/institution/campus name
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Distinct tenants</div>
                  <div className="font-display font-bold text-2xl" data-testid="preview-distinct-tenants">{distinctTenants}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Distinct institutions</div>
                  <div className="font-display font-bold text-2xl" data-testid="preview-distinct-institutions">{distinctInsts}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Total campuses</div>
                  <div className="font-display font-bold text-2xl" data-testid="preview-total-campuses">{validRows.length}</div>
                </div>
              </div>

              <div className="border rounded-md max-h-96 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      {ALL_COLS.map((c) => <TableHead key={c} className="font-mono text-[11px]">{c}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validRows.slice(0, 200).map((r, i) => (
                      <TableRow key={i} data-testid={`preview-row-${i}`}>
                        {ALL_COLS.map((c) => <TableCell key={c} className="text-xs">{r[c] || <span className="text-muted-foreground">—</span>}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {validRows.length > 200 && (
                <div className="text-xs text-muted-foreground text-center">(showing first 200 rows — all {validRows.length} will be imported)</div>
              )}

              <Button
                onClick={doImport}
                disabled={!validRows.length || missing.length > 0 || loading}
                size="lg"
                className="w-full sm:w-auto"
                data-testid="run-import-button"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Import {validRows.length} campus{validRows.length === 1 ? "" : "es"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4 — results */}
        {result && (
          <Card data-testid="import-result">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Import complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryStat label="Tenants created" value={result.tenants_created} />
                <SummaryStat label="Institutions created" value={result.institutions_created} />
                <SummaryStat label="Campuses created" value={result.campuses_created} accent="emerald" />
                <SummaryStat label="Already existed" value={result.campuses_reused} accent="muted" />
              </div>
              {result.errors > 0 && (
                <div className="text-sm border border-destructive/40 bg-destructive/5 text-destructive p-3 rounded-md">
                  {result.errors} row{result.errors === 1 ? "" : "s"} failed — see the table below
                </div>
              )}
              <div className="border rounded-md max-h-96 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Campus</TableHead>
                      <TableHead>Institution</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Portal / Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((r) => (
                      <TableRow key={r.row_index}>
                        <TableCell className="font-mono text-xs">{r.row_index + 1}</TableCell>
                        <TableCell className="text-xs">{r.campus_name}</TableCell>
                        <TableCell className="text-xs">{r.institution_name}</TableCell>
                        <TableCell className="text-xs">{r.tenant_name}</TableCell>
                        <TableCell>
                          {r.status === "created" && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">created</Badge>}
                          {r.status === "existing" && <Badge variant="secondary">existing</Badge>}
                          {r.status === "error" && <Badge variant="destructive">error</Badge>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {r.portal_path ? <a href={r.portal_path} target="_blank" rel="noreferrer" className="text-primary hover:underline">{r.portal_path}</a> : (r.error || "—")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

function SummaryStat({ label, value, accent }) {
  const color =
    accent === "emerald" ? "text-emerald-600" :
    accent === "muted" ? "text-muted-foreground" :
    "text-foreground";
  return (
    <div className="rounded-md border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display font-bold text-2xl ${color}`}>{value}</div>
    </div>
  );
}
